import { useCallback, useState } from "react";

export interface PaginationMeta {
  page: number;
  pageCount: number;
  total: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    pagination: PaginationMeta;
  };
}

export interface UseFilterPaginationProps<T> {
  initialContent: PaginatedResponse<T>;
  fetchFunction: (
    filter: string,
    page: number,
  ) => Promise<PaginatedResponse<T>>;
  initialFilter?: string;
}

export interface UseFilterPaginationReturn<T> {
  currentContent: PaginatedResponse<T>;
  activeFilter: string;
  handleChangeFilter: (filter: string) => void;
  handleClickOnNext: () => void;
  handleClickOnPrev: () => void;
  handleClickOnPagination: (pageNumber: number) => void;
}

export const useFilterPagination = <T>({
  initialContent,
  fetchFunction,
  initialFilter = "all",
}: UseFilterPaginationProps<T>): UseFilterPaginationReturn<T> => {
  const [currentContent, setCurrentContent] = useState(initialContent);
  const [activeFilter, setActiveFilter] = useState<string>(initialFilter);

  const getNewContent = useCallback(
    async (filter: string, page: number) => {
      const content = await fetchFunction(filter, page);
      setCurrentContent(content);
    },
    [fetchFunction],
  );

  const handleClickOnNext = useCallback(() => {
    const currentPage =
      currentContent.meta.pagination.page !==
        currentContent.meta.pagination.pageCount &&
      currentContent.meta.pagination.page + 1;

    if (currentPage) {
      getNewContent(activeFilter, currentPage);
    }
  }, [currentContent?.meta, activeFilter, getNewContent]);

  const handleClickOnPrev = useCallback(() => {
    const currentPage =
      currentContent.meta.pagination.page !== 1 &&
      currentContent.meta.pagination.page - 1;

    if (currentPage) {
      getNewContent(activeFilter, currentPage);
    }
  }, [currentContent?.meta, activeFilter, getNewContent]);

  const handleClickOnPagination = useCallback(
    (pageNumber: number) => {
      getNewContent(activeFilter, pageNumber);
    },
    [getNewContent, activeFilter],
  );

  const handleChangeFilter = useCallback(
    (filter: string) => {
      setActiveFilter(filter);
      getNewContent(filter, 1);
    },
    [getNewContent],
  );

  return {
    currentContent,
    activeFilter,
    handleChangeFilter,
    handleClickOnNext,
    handleClickOnPrev,
    handleClickOnPagination,
  };
};
